import { Injectable } from '@angular/core';
import {
  LabelInfo,
  TranslationInfo,
} from 'src/app/models/label-info.model';
import { Language } from '../../../../../models/language.model';
import { ErrorTranslations } from '../../../../../models/forms.model';
import { StepControlType } from '../flux.model';

@Injectable({
  providedIn: 'root',
})
export class TranslationsHelper {

  public static createComponentErrorsTranslations(
    langs: Language[],
    componentType: StepControlType
  ): ErrorTranslations[] {
    switch (componentType) {
      case StepControlType.ManualInput: return this.createInputErrorsTranslations(langs);
      case StepControlType.CheckBox:
      case StepControlType.Dictionary: return this.createDefaultErrorsTranslations(langs);
      case StepControlType.DownloadDocuments: return this.createDownloadErrorsTranslations(langs);
      case StepControlType.UploadDocuments: return this.createUploadErrorsTranslations(langs);
      case StepControlType.ViewDocuments: return this.createViewErrorsTranslations(langs);
    }
    return [];
  }

  private static createDownloadErrorsTranslations(
    langs: Language[]
  ): ErrorTranslations[] {
    const tInfo: ErrorTranslations[] = [
      {
        code: 'failToDownload',
        translations: this.createLabelsInfo(langs, 'Failed to download.', 'Nu am reușit sa descarc.')
      },
      {
        code: 'noDocumentsToDownload',
        translations: this.createLabelsInfo(langs, 'No documents to download.', 'Nu sunt documente de descărcat.')
      },
    ];
    return tInfo;
  }

  private static createUploadErrorsTranslations(
    langs: Language[]
  ): ErrorTranslations[] {
    const tInfo: ErrorTranslations[] = [
      {
        code: 'invalidType',
        translations: this.createLabelsInfo(langs, 'Invalid type', 'Tip invalid')
      },
      {
        code: 'maxSize',
        translations: this.createLabelsInfo(langs, 'The item exceeds the maximum size of {0}.', 'Fișierul depașeste dimensiunea maxima de {0}.')
      },
      {
        code: 'isMandatory',
        translations: this.createLabelsInfo(langs, 'The item is mandatory.', 'Acest fișier este obligatoriu.')
      },
      {
        code: 'waitForUpload',
        translations: this.createLabelsInfo(langs, 'Please wait for upload to finish.', 'Vă rugam așteptați incărcarea fișierelor.')
      },
    ];

    return tInfo;
  }

  private static createViewErrorsTranslations(
    langs: Language[]
  ): ErrorTranslations[] {
    const tInfo: ErrorTranslations[] = [
      {
        code: 'failToView',
        translations: this.createLabelsInfo(langs, 'Failed to view', 'Nu am reușit sa vizualizez')
      },
      {
        code: 'noDocumentsToShow',
        translations: this.createLabelsInfo(langs, 'No documents to show', 'Nu sunt documente de afișat')
      },
    ];
    return tInfo;
  }

  private static createInputErrorsTranslations(
    langs: Language[]
  ): ErrorTranslations[] {
    const tInfo: ErrorTranslations[] = [
      {
        code: 'isMandatoryValidation',
        translations: this.createLabelsInfo(langs, 'This field is required.', 'Acest câmp este obligatoriu.')
      },
      {
        code: 'simpleValidation',
        translations: this.createLabelsInfo(langs, 'This field is invalid.', 'Acest câmp este invalid.')
      },
      {
        code: 'throughApiValidation',
        translations: this.createLabelsInfo(langs, 'This field is invalid.', 'Acest câmp este invalid.')
      },
    ];
    return tInfo;
  }

  private static createDefaultErrorsTranslations(
    langs: Language[]
  ): ErrorTranslations[] {
    const tInfo: ErrorTranslations[] = [
      {
        code: 'isMandatoryValidation',
        translations: this.createLabelsInfo(langs, 'This field is required.', 'Acest câmp este obligatoriu.')
      }
    ];
    return tInfo;
  }

  public static createCheckEmailTranslations(
    langs: Language[]
  ): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'Check email',
        'Verifica email'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        '',
        ''
      ),
      this.createTranslation(
        langs,
        'emailLabel',
        'flux.systemSteps.email.emailLabel',
        'Email',
        'Email'
      ),
      this.createTranslation(
        langs,
        'emailButtonLabel',
        'flux.systemSteps.email.emailButtonLabel',
        'Send code',
        'Trimite cod'
      ),
      this.createTranslation(
        langs,
        'otpLabel',
        'flux.systemSteps.email.otpLabel',
        'Verification code',
        'Cod de verificare'
      ),
      this.createTranslation(
        langs,
        'otpButtonLabel',
        'flux.systemSteps.email.otpButtonLabel',
        'Verify',
        'Verifică'
      ),
      this.createTranslation(
        langs,
        'emailRequiredLabel',
        'flux.components.email.emailRequiredLabel',
        'Email is required',
        'Email-ul este obligatoriu'
      ),
      this.createTranslation(
        langs,
        'emailErrorLabel',
        'flux.systemSteps.email.emailErrorLabel',
        'Email is not valid',
        'Email-ul nu este valid'
      ),
      this.createTranslation(
        langs,
        'otpErrorLabel',
        'flux.systemSteps.email.otpErrorLabel',
        'Code verification error',
        'Eroare la verificare cod'
      ),
      this.createTranslation(
        langs,
        'otpSuccessLabel',
        'flux.systemSteps.email.otpSuccessLabel',
        'Code verification successful',
        'Verificarea codului a fost realizată cu succes'
      ),
    ];
    return tInfo;
  }

  public static createCheckPhoneTranslations(
    langs: Language[]
  ): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'Check phone',
        'Verifica telefon'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        '',
        ''
      ),
      this.createTranslation(
        langs,
        'phoneLabel',
        'flux.systemSteps.phone.phoneLabel',
        'Phone',
        'Telefon'
      ),
      this.createTranslation(
        langs,
        'phoneButtonLabel',
        'flux.systemSteps.phone.phoneButtonLabel',
        'Send code',
        'Trimite cod'
      ),
      this.createTranslation(
        langs,
        'otpLabel',
        'flux.systemSteps.email.otpLabel',
        'Verification code',
        'Cod de verificare'
      ),
      this.createTranslation(
        langs,
        'otpButtonLabel',
        'flux.systemSteps.email.otpButtonLabel',
        'Verify',
        'Verifică'
      ),
      this.createTranslation(
        langs,
        'phoneRequiredLabel',
        'flux.components.phone.phoneRequiredLabel',
        'Phone number is required',
        'Numărul de telefon este obligatoriu'
      ),
      this.createTranslation(
        langs,
        'phoneErrorLabel',
        'flux.systemSteps.phone.phoneErrorLabel',
        'Phone number is not valid',
        'Numărul de telefon nu este valid'
      ),
      this.createTranslation(
        langs,
        'otpErrorLabel',
        'flux.systemSteps.email.otpErrorLabel',
        'Code verification error',
        'Eroare la verificare cod'
      ),
      this.createTranslation(
        langs,
        'otpSuccessLabel',
        'flux.systemSteps.email.otpSuccessLabel',
        'Code verification successful',
        'Verificarea codului a fost realizată cu succes'
      ),
    ];
    return tInfo;
  }

  public static createIdScanTranslations(langs: Language[]): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'Id scan',
        'Scanare act identitate'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        'Step to scan id document',
        'Pas pentru încarcarea documentului de identitate'
      ),
      this.createTranslation(
        langs,
        'uploadFileButtonLabel',
        'flux.systemSteps.idScan.uploadButtonTextLabel',
        'Upload file',
        'Încarcă fișier'
      ),
      this.createTranslation(
        langs,
        'takePictureButtonLabel',
        'flux.systemSteps.idScan.takePictureButtonTextLabel',
        'Take picture',
        'Realizează o fotografie'
      ),
    ];
    return tInfo;
  }

  public static createPauseTranslations(langs: Language[]): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'Pause',
        'Pauză'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        '',
        ''
      ),

      this.createTranslation(
        langs,
        'offerReadyMessage',
        'flux.systemSteps.pause.offerReadyMessageLabel',
        'Your offer has been processed. Click on the link below to continue the flow.',
        'Oferta dumneavoastra a fost procesata. Click pe link-ul de mai jos pentru a continua fluxul.'
      ),

      this.createTranslation(
        langs,
        'continueOffersLinkMessageLabel',
        'flux.systemSteps.pause.continueOffersLinkMessageLabel',
        'Continue flow',
        'Continuare flux'
      ),

      this.createTranslation(
        langs,
        'offerReadySmsMessageLabel',
        'flux.systemSteps.pause.offerReadySmsMessageLabel',
        'Your offer has been processed. Click on the link below to continue the flow.',
        'Oferta dumneavoastra a fost procesata. Click pe link-ul de mai jos pentru a continua fluxul.'
      ),
    ];
    return tInfo;
  }

  public static createActionTranslations(langs: Language[]): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'loadingMessageTranslations',
        'appConfigEdit.flux.customStep.loadingMessage',
        'Loading message',
        'Mesaj de așteptare'
      ),
    ];
    return tInfo;
  }

  public static createValidationTranslations(langs: Language[]): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'loadingMessageTranslations',
        'appConfigEdit.flux.customStep.loadingMessage',
        'Validating data...',
        'Se validează datele...'
      ),
    ];
    return tInfo;
  }

  public static createStatusTranslations(langs: Language[]): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'loadingMessageTranslations',
        'appConfigEdit.flux.customStep.loadingMessage',
        'Loading message',
        'Mesaj de așteptare'
      ),
    ];
    return tInfo;
  }

  public static createKycTranslations(langs: Language[]): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(langs, 'name', 'common.others.name', 'KYC', 'KYC'),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        '',
        ''
      ),
      this.createTranslation(
        langs,
        'loadingMessageTranslations',
        'appConfigEdit.flux.customStep.loadingMessage',
        'Loading identification step...',
        'Se încarcă pasul de identificare...'
      ),
    ];
    return tInfo;
  }

  public static createPaymentsTranslations(
    langs: Language[]
  ): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'Payment',
        'Plată'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        '',
        ''
      ),
      this.createTranslation(
        langs,
        'cardenrolButtonLabel',
        'cardenrol.cardenrolButtonLabel',
        'Enrol card',
        'Înrolează card'
      ),
      this.createTranslation(
        langs,
        'cardenrolFailureLabel',
        'cardenrol.cardenrolFailureLabel',
        'Error enrolling card',
        'Eroare la înrolare card'
      ),
      this.createTranslation(
        langs,
        'cardenrolSuccessLabel',
        'cardenrol.cardenrolSuccessLabel',
        'Success enrolling card',
        'Succes la înrolare card'
      ),
    ];
    return tInfo;
  }

  public static createOpenBankingTranslations(
    langs: Language[]
  ): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'Open Banking',
        'Open Banking'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        'Connect your bank account',
        'Conectați contul bancar'
      ),
      this.createTranslation(
        langs,
        'initialMessageTranslations',
        'flux.systemSteps.openBanking.initialMessage',
        'To continue, we need to verify your bank account information. Click Next to proceed.',
        'Pentru a continua, trebuie să verificăm informațiile contului dvs. bancar. Faceți clic pe Următorul pentru a continua.'
      ),
      this.createTranslation(
        langs,
        'waitingMessageTranslations',
        'flux.systemSteps.openBanking.waitingMessage',
        'Please wait while we retrieve your account information...',
        'Vă rugăm să așteptați până când preluăm informațiile contului dvs...'
      ),
      this.createTranslation(
        langs,
        'consentErrorMessageTranslations',
        'flux.systemSteps.openBanking.consentErrorMessage',
        'You must provide consent to continue. Please try again.',
        'Trebuie să oferiți consimțământul pentru a continua. Vă rugăm să încercați din nou.'
      ),
      this.createTranslation(
        langs,
        'completionMessageTranslations',
        'flux.systemSteps.openBanking.completionMessage',
        'Account information successfully retrieved.',
        'Informațiile contului au fost preluate cu succes.'
      ),
      this.createTranslation(
        langs,
        'retryButtonLabelTranslations',
        'flux.systemSteps.openBanking.retryButtonLabel',
        'Retry',
        'Reîncearcă'
      ),
      // Button translations
      this.createTranslation(
        langs,
        'nextButtonTranslations',
        'flux.systemSteps.openBanking.nextButton',
        'Next',
        'Următorul'
      ),
      this.createTranslation(
        langs,
        'continueButtonTranslations',
        'flux.systemSteps.openBanking.continueButton',
        'Continue',
        'Continuă'
      ),
      // UI labels
      this.createTranslation(
        langs,
        'selectBankTranslations',
        'flux.systemSteps.openBanking.selectBank',
        'Select your bank',
        'Selectați banca dvs.'
      ),
      this.createTranslation(
        langs,
        'consentTitleTranslations',
        'flux.systemSteps.openBanking.consentTitle',
        'Bank Authorization',
        'Autorizare bancară'
      ),
      this.createTranslation(
        langs,
        'consentDescriptionTranslations',
        'flux.systemSteps.openBanking.consentDescription',
        'Please complete the authorization process with your bank',
        'Vă rugăm să completați procesul de autorizare cu banca dvs.'
      ),
      this.createTranslation(
        langs,
        'waitingForAuthorizationTranslations',
        'flux.systemSteps.openBanking.waitingForAuthorization',
        'Waiting for authorization...',
        'Se așteaptă autorizarea...'
      ),
      this.createTranslation(
        langs,
        'popupWindowMessageTranslations',
        'flux.systemSteps.openBanking.popupWindowMessage',
        'A new window has opened for bank authorization. Please complete the process there.',
        'S-a deschis o fereastră nouă pentru autorizare bancară. Vă rugăm să completați procesul acolo.'
      ),
      this.createTranslation(
        langs,
        'popupBlockerMessageTranslations',
        'flux.systemSteps.openBanking.popupBlockerMessage',
        'If the window didn\'t open, please check your popup blocker settings.',
        'Dacă fereastra nu s-a deschis, vă rugăm să verificați setările blocatorului de popup-uri.'
      ),
      this.createTranslation(
        langs,
        'accountsRetrievedTranslations',
        'flux.systemSteps.openBanking.accountsRetrieved',
        'Accounts retrieved',
        'Conturi preluate'
      ),
      this.createTranslation(
        langs,
        'transactionsRetrievedTranslations',
        'flux.systemSteps.openBanking.transactionsRetrieved',
        'Transactions retrieved',
        'Tranzacții preluate'
      ),
      this.createTranslation(
        langs,
        'accountsFoundTranslations',
        'flux.systemSteps.openBanking.accountsFound',
        '{0} account(s) found',
        '{0} cont(uri) găsite'
      ),
      this.createTranslation(
        langs,
        'transactionsSummaryTranslations',
        'flux.systemSteps.openBanking.transactionsSummary',
        '{0} transaction(s) retrieved for the last {1} month(s)',
        '{0} tranzacție(i) preluate pentru ultimele {1} lună(i)'
      ),
    ];
    return tInfo;
  }

  public static createOmniCheckPersonTranslations(
    langs: Language[]
  ): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'OmniCheck - Individual',
        'OmniCheck - Persoana fizica'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        'Watchlist check - individual',
        'Verificare watchlist persoana fizica'
      ),
      this.createTranslation(
        langs,
        'waitingMessageTranslations',
        'flux.systemSteps.omniCheckPerson.waitingMessage',
        'Please wait while we verify your identity...',
        'Va rugam asteptati cat timp verificam identitatea dumneavoastra...'
      ),
    ];
    return tInfo;
  }

  public static createOmniCheckBusinessTranslations(
    langs: Language[]
  ): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'OmniCheck - Legal Entity',
        'OmniCheck - Persoana juridica'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        'Watchlist check - legal entity',
        'Verificare watchlist persoana juridica'
      ),
      this.createTranslation(
        langs,
        'waitingMessageTranslations',
        'flux.systemSteps.omniCheckBusiness.waitingMessage',
        'Please wait while we verify your company...',
        'Va rugam asteptati cat timp verificam compania dumneavoastra...'
      ),
    ];
    return tInfo;
  }

  public static createCustomStepTranslations(
    langs: Language[]
  ): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'Custom step',
        'Pas custom'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        '',
        ''
      ),
    ];
    return tInfo;
  }

  public static createViewOfferStepTranslations(
    langs: Language[]
  ): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'View offer',
        'Vizualizare ofertă'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        'View offer step',
        'Pas pentru vizualizare ofertă'
      ),
    ];
    return tInfo;
  }

  public static createSignTranslations(langs: Language[]): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'Sign documents',
        'Semnare documente'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        'Sign documents step',
        'Pas pentru semnare documente'
      ),
    ];
    return tInfo;
  }

  public static createGenerateDocumentsTranslations(
    langs: Language[]
  ): TranslationInfo[] {
    const tInfo: TranslationInfo[] = [
      this.createTranslation(
        langs,
        'name',
        'common.others.name',
        'Generate documents',
        'Generare documente'
      ),
      this.createTranslation(
        langs,
        'description',
        'common.others.description',
        'Generate documents step',
        'Pas pentru generare documente'
      ),
    ];
    return tInfo;
  }

  private static createTranslation(
    langs: Language[],
    name: string,
    translationKey: string,
    enValue: string,
    roValue: string
  ): TranslationInfo {
    return {
      name: name,
      translationKey: translationKey,
      values: this.createLabelsInfo(langs, enValue, roValue),
    };
  }

  public static createLabelsInfo(supportedLanguages: Language[], enValue: string, roValue: string): LabelInfo[] {
    return this.createLabelsInfoCore(supportedLanguages, [
      { iso: 'en', value: enValue },
      { iso: 'ro', value: roValue },
    ]);
  }

  private static createLabelsInfoCore(
    supportedLanguages: Language[],
    values: { iso: string; value: string }[]
  ): LabelInfo[] {
    let list: LabelInfo[] = [];
    values.forEach((v) => {
      const langId = supportedLanguages.find((lang) => lang.iso === v.iso)?.id;
      if (langId && langId > 0) {
        list.push({
          languageId: langId,
          languageIso: v.iso,
          value: v.value,
        });
      }
    });
    return list;
  }
}
